import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined'
import {
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { numberFormat } from '../../../../../utils'
import { IOrderReportBucket } from '../types'

interface Props {
  summary?: IOrderReportBucket
}

// แถวไล่ยอดแบบใบแจ้งยอด: ราคาเต็ม → ยอดขาย → หักค่าธรรมเนียม → ยอดที่ได้รับจริง
interface IRow {
  id: string
  label: string
  value: number
  variant?: 'subtotal' | 'grand' // subtotal = ยอดรวมย่อย (เส้นบน), grand = ยอดสุดท้าย
  indent?: boolean // รายการย่อยของ parent (แสดงเมื่อ parent ถูกกางเท่านั้น)
  parent?: string // id ของแถวแม่ (ใส่คู่กับ indent)
  collapsible?: boolean // แถวแม่ที่มีรายการย่อย → แสดงปุ่ม +/- กาง/ยุบ
  showPct?: boolean
  hint?: string
}

const SettlementBreakdownTable = ({ summary }: Props) => {
  const get = (key: keyof IOrderReportBucket) => Number(summary?.[key] ?? 0)

  // เก็บ id ของแถวแม่ที่ถูกกาง (default ยุบทั้งหมด)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const isOpen = (id: string) => !!expanded[id]
  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const revenue = get('revenueTotal')
  const shippingFee = get('shippingFeeTotal')
  const shippingSubsidy = get('shippingSubsidyTotal')
  const shippingNet = shippingFee + shippingSubsidy

  const rows: IRow[] = [
    { id: 'gross', label: 'ราคาเต็ม (ก่อนส่วนลด)', value: get('grossSalesTotal') },
    { id: 'sellerDiscount', label: 'ส่วนลดร้าน', value: get('sellerDiscountTotal') },
    {
      id: 'salesRefund',
      label: 'ยอดขายที่ยกเลิก/คืน',
      value: get('salesRefundTotal'),
      collapsible: true,
      hint: 'ออเดอร์ที่คืนเงินจะถูกหักยอดขายออกทั้งใบ',
    },
    {
      id: 'salesRefundGross',
      label: 'ราคาเต็มส่วนที่คืน',
      value: get('salesRefundGrossTotal'),
      indent: true,
      parent: 'salesRefund',
    },
    {
      id: 'salesRefundSeller',
      label: 'ส่วนลดร้านส่วนที่คืน',
      value: get('salesRefundSellerTotal'),
      indent: true,
      parent: 'salesRefund',
    },
    {
      id: 'revenue',
      label: 'ยอดขาย',
      value: revenue,
      variant: 'subtotal',
      showPct: true,
      hint: 'ฐานที่ TikTok ใช้คำนวณค่าธรรมเนียม',
    },
    {
      id: 'commission',
      label: 'ค่าคอมมิชชั่น',
      value: get('commissionTotal'),
      showPct: true,
    },
    {
      id: 'affiliate',
      label: 'ค่า affiliate',
      value: get('affiliateTotal'),
      collapsible: true,
      showPct: true,
    },
    {
      id: 'affiliateCommission',
      label: 'ค่าคอม affiliate',
      value: get('affiliateCommissionTotal'),
      indent: true,
      parent: 'affiliate',
    },
    {
      id: 'affiliateAds',
      label: 'ค่าโฆษณา affiliate',
      value: get('affiliateAdsTotal'),
      indent: true,
      parent: 'affiliate',
    },
    {
      id: 'affiliatePartner',
      label: 'ค่าพาร์ทเนอร์ affiliate',
      value: get('affiliatePartnerTotal'),
      indent: true,
      parent: 'affiliate',
    },
    {
      id: 'shippingNet',
      label: 'ค่าส่งสุทธิ',
      value: shippingNet,
      collapsible: true,
      showPct: true,
    },
    {
      id: 'shippingFee',
      label: 'ค่าส่งที่ร้านจ่าย',
      value: shippingFee,
      indent: true,
      parent: 'shippingNet',
    },
    {
      id: 'shippingSubsidy',
      label: 'ส่วนลดค่าส่งจาก TikTok',
      value: shippingSubsidy,
      indent: true,
      parent: 'shippingNet',
    },
    {
      id: 'otherFee',
      label: 'ค่าธรรมเนียมอื่น',
      value: get('otherFeeTotal'),
      showPct: true,
      hint: 'ค่าธรรมเนียมโปรแกรมส่งฟรีและค่าธรรมเนียมธุรกรรม — TikTok รวมมาในยอดเดียว ไม่ได้แยกรายการมาให้',
    },
    {
      id: 'feeTotal',
      label: 'ค่าธรรมเนียมรวม',
      value: get('feeTotal'),
      variant: 'subtotal',
      showPct: true,
    },
    {
      id: 'net',
      label: 'ยอดที่ได้รับจริง',
      value: get('netTotal'),
      variant: 'grand',
      showPct: true,
    },
  ]

  // ข้อมูลประกอบ — ไม่อยู่ในสายคำนวณ (ยอดขาย + ค่าธรรมเนียม = ยอดที่ได้รับจริง ปิดพอดีแล้ว)
  const infoRows: IRow[] = [
    {
      id: 'customerPayment',
      label: 'ลูกค้าจ่ายจริง',
      value: get('customerPaymentTotal'),
      hint: 'ฝั่งผู้ซื้อ — รวมค่าส่งที่ลูกค้าจ่าย จึงอาจมากกว่ายอดขาย',
    },
    {
      id: 'platformDiscount',
      label: 'ส่วนลดจาก TikTok',
      value: get('platformDiscountTotal'),
      hint: 'คูปองที่ TikTok ออกให้ลูกค้า — ไม่ได้หักจากร้าน',
    },
    {
      id: 'refund',
      label: 'เงินคืนลูกค้า',
      value: get('refundTotal'),
      hint: 'ออเดอร์ที่คืนเงินถูกหักยอดขายออกไปแล้ว จึงไม่ถูกหักซ้ำจากยอดที่ได้รับจริง',
    },
    { id: 'adjustment', label: 'ปรับปรุงยอด', value: get('adjustmentTotal') },
  ]

  // ซ่อนรายการย่อยเมื่อแถวแม่ยังไม่ถูกกาง
  const visibleRows = rows.filter((row) => !row.parent || isOpen(row.parent))

  // ยอดขาย ฿0 (ยังไม่มีออเดอร์ที่จ่ายแล้ว) → หาร 0 ไม่ได้
  const pct = (value: number) =>
    revenue ? `${numberFormat((value / revenue) * 100, 1)}%` : '—'

  const amountColor = (value: number) => {
    if (value < 0) return 'error.main'
    if (value > 0) return 'success.main'
    return 'text.disabled'
  }

  const renderRow = (row: IRow) => {
    const isSubtotal = row.variant === 'subtotal'
    const isGrand = row.variant === 'grand'
    const bold = isSubtotal || isGrand
    const open = isOpen(row.id)

    return (
      <TableRow
        key={row.id}
        sx={{
          ...(bold && { '& td': { borderTop: '2px solid' } }),
          ...(isGrand && { backgroundColor: 'action.hover' }),
        }}
      >
        <TableCell sx={{ pl: row.indent ? 4 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {row.collapsible && (
              <IconButton
                size="small"
                sx={{ p: 0, mr: 0.5 }}
                onClick={() => toggle(row.id)}
              >
                {open ? (
                  <IndeterminateCheckBoxOutlinedIcon
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                  />
                ) : (
                  <AddBoxOutlinedIcon
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                  />
                )}
              </IconButton>
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: bold ? 600 : 400,
                color: row.indent ? 'text.secondary' : 'text.primary',
              }}
            >
              {row.label}
            </Typography>
            {row.hint && (
              <Tooltip title={row.hint} arrow>
                <HelpOutlineIcon
                  sx={{ fontSize: 14, color: 'text.disabled' }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontWeight: bold ? 600 : 400,
            color: isGrand ? 'success.main' : amountColor(row.value),
          }}
        >
          {numberFormat(row.value)}
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontWeight: bold ? 600 : 400, color: 'text.secondary' }}
        >
          {row.showPct ? pct(row.value) : ''}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        รายละเอียดการชำระเงิน
      </Typography>
      <Typography variant="caption" color="text.secondary">
        เฉพาะออเดอร์ที่ TikTok จ่ายเงินแล้ว ({numberFormat(get('settledCount'), 0)}{' '}
        ออเดอร์)
      </Typography>

      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>รายการ</TableCell>
            <TableCell align="right">จำนวนเงิน (฿)</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              % ของยอดขาย
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map(renderRow)}

          <TableRow>
            <TableCell colSpan={3} sx={{ pt: 3, pb: 0.5, border: 0 }}>
              <Typography variant="caption" color="text.secondary">
                ข้อมูลประกอบ (ไม่อยู่ในสายคำนวณด้านบน)
              </Typography>
            </TableCell>
          </TableRow>
          {infoRows.map(renderRow)}
        </TableBody>
      </Table>
    </Card>
  )
}

export default SettlementBreakdownTable
